/**
 * FormSection — titled section with description and responsive field grid
 *
 * @example
 * <FormSection
 *   title={t('admin.section_basic_info')}
 *   description={t('admin.section_basic_info_desc')}
 *   columns={2}
 * >
 *   <FormField label={t('admin.col_name')}>
 *     <Input ... />
 *   </FormField>
 *   <FormField label={t('admin.col_email')} required>
 *     <Input ... />
 *   </FormField>
 * </FormSection>
 */

import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';

// ─── FormField helper ─────────────────────────────────────────────────────────
export function FormField({ label, description, required, error, children, className, span }) {
  return (
    <div className={cn('space-y-1.5', span === 2 && 'col-span-2', className)}>
      {label && (
        <label className="block text-sm font-bold text-[var(--text-main)]">
          {label}
          {required && <span className="ml-1 text-[var(--brand-danger)]" aria-hidden>*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-[var(--text-muted)]">{description}</p>
      )}
      {children}
      {error && (
        <p className="text-xs text-[var(--brand-danger)] font-semibold" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

FormField.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  /** Span full width in a 2-col grid */
  span: PropTypes.number,
};

// ─── FormSection ──────────────────────────────────────────────────────────────
export default function FormSection({
  title,
  description,
  columns,
  children,
  className,
  actions,
}) {
  const colClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2';

  return (
    <section
      className={cn(
        'bg-white rounded-2xl border-2 border-[var(--border-light)] p-5',
        className,
      )}
    >
      {(title || description) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {title && (
              <h2 className="font-display text-base font-bold text-[var(--text-main)]">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-sm text-[var(--text-muted)] mt-0.5">{description}</p>
            )}
          </div>
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </div>
      )}
      <div className={cn('grid gap-4', colClass)}>{children}</div>
    </section>
  );
}

FormSection.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  /** Number of grid columns (1-4) */
  columns: PropTypes.oneOf([1, 2, 3, 4]),
  children: PropTypes.node,
  className: PropTypes.string,
  actions: PropTypes.node,
};

FormSection.defaultProps = {
  columns: 2,
};
